/**
 * Referrals Page
 * Feb 2026 — audit view for the referral system. Admin/super-admin only.
 * Every attempt (credited or blocked) is logged; this page is the
 * clawback surface for abuse review.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '../AdminApp';
import { toast } from 'sonner';
import {
  Search,
  ChevronLeft,
  ChevronRight,
  CheckCircle,
  XCircle,
  UserPlus,
} from 'lucide-react';

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

const badgeForReason = (r) => {
  if (!r) return null;
  const label = r.replace(/_/g, ' ');
  return (
    <span className="inline-block px-2 py-0.5 rounded text-[11px] bg-red-500/10 text-red-300 border border-red-500/30">
      {label}
    </span>
  );
};

const displayHandle = (h) => (h ? (h.startsWith('@') ? h : `@${h}`) : '—');

const ReferralsPage = () => {
  const { api } = useAdmin();
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [pendingQ, setPendingQ] = useState('');
  const [credited, setCredited] = useState('all');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const perPage = 20;
  const totalPages = Math.max(1, Math.ceil(total / perPage));

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: perPage, credited };
      if (q) params.q = q;
      const res = await api.get('/api/admin/referrals', { params });
      setRecords(res.data?.records || []);
      setTotal(res.data?.total || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load referrals');
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [api, q, credited, page]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    setPage(1);
    setQ(pendingQ.trim());
  };

  const creditedCount = records.filter(r => r.credited).length;
  const blockedCount = records.filter(r => !r.credited).length;

  return (
    <div className="p-4 md:p-6" data-testid="referrals-page">
      <div className="max-w-7xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <UserPlus className="w-6 h-6 text-emerald-400" />
            Referrals
          </h1>
          <p className="text-sm text-gray-400 mt-1">
            Every referral attempt — credited or blocked — is logged here
            for audit and clawback. Device-id and IP checks deter casual
            farming but aren&apos;t bulletproof; review anomalies manually.
          </p>
        </div>

        <form onSubmit={submitSearch} className="mb-4 flex flex-col md:flex-row gap-2">
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={pendingQ}
              onChange={(e) => setPendingQ(e.target.value)}
              placeholder="Search @handle, name, user id, or referral code"
              data-testid="referrals-search-input"
              className="w-full pl-9 pr-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm focus:outline-none focus:border-emerald-500"
            />
          </div>
          <select
            value={credited}
            onChange={(e) => { setCredited(e.target.value); setPage(1); }}
            className="px-3 py-2 bg-slate-800 border border-slate-700 rounded-lg text-white text-sm"
            data-testid="referrals-credited-filter"
          >
            <option value="all">All statuses</option>
            <option value="yes">Credited only</option>
            <option value="no">Blocked only</option>
          </select>
          <button
            type="submit"
            data-testid="referrals-search-btn"
            className="px-4 py-2 bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium rounded-lg"
          >
            Search
          </button>
          {q && (
            <button
              type="button"
              onClick={() => { setPendingQ(''); setQ(''); setPage(1); }}
              className="px-3 py-2 bg-slate-800 hover:bg-slate-700 text-gray-300 text-sm rounded-lg"
            >
              Clear
            </button>
          )}
        </form>

        <div className="text-xs text-gray-500 mb-3 flex items-center gap-3" data-testid="referrals-summary">
          <span>{loading ? 'Loading…' : `${total} attempt${total === 1 ? '' : 's'}${q ? ` for "${q}"` : ''}`}</span>
          {!loading && records.length > 0 && (
            <>
              <span className="text-emerald-400">{creditedCount} credited</span>
              <span className="text-red-400">{blockedCount} blocked</span>
            </>
          )}
        </div>

        <div className="bg-slate-900/60 border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/70 text-gray-300">
              <tr>
                <th className="text-left px-3 py-2">Date</th>
                <th className="text-left px-3 py-2">Referrer</th>
                <th className="text-left px-3 py-2">Referred</th>
                <th className="text-left px-3 py-2">Code</th>
                <th className="text-left px-3 py-2">Device</th>
                <th className="text-left px-3 py-2">IP</th>
                <th className="text-left px-3 py-2">Reward</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {loading && (
                <tr><td colSpan={8} className="text-center py-6 text-gray-500">Loading…</td></tr>
              )}
              {!loading && records.length === 0 && (
                <tr><td colSpan={8} className="text-center py-6 text-gray-500" data-testid="referrals-empty">No referrals yet.</td></tr>
              )}
              {!loading && records.map((r) => (
                <tr key={r.id} className="hover:bg-slate-800/40" data-testid={`referral-row-${r.id}`}>
                  <td className="px-3 py-2 text-gray-400 text-xs">{formatDate(r.created_at)}</td>
                  <td className="px-3 py-2 text-white text-xs">
                    <div className="font-medium">{r.referrer?.name || '—'}</div>
                    <div className="text-gray-500">{displayHandle(r.referrer?.handle)}</div>
                  </td>
                  <td className="px-3 py-2 text-white text-xs">
                    <div className="font-medium">{r.referred?.name || '—'}</div>
                    <div className="text-gray-500">{displayHandle(r.referred?.handle)}</div>
                  </td>
                  <td className="px-3 py-2 font-mono text-emerald-300 text-xs">{r.referral_code}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs font-mono">{r.device_id || '—'}</td>
                  <td className="px-3 py-2 text-gray-400 text-xs font-mono">{r.ip || '—'}</td>
                  <td className="px-3 py-2 text-emerald-300 text-xs">{r.reward}</td>
                  <td className="px-3 py-2 text-xs">
                    {r.credited ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400" data-testid="referral-credited-yes">
                        <CheckCircle className="w-3 h-3" /> Credited
                      </span>
                    ) : (
                      <div className="flex flex-col gap-0.5" data-testid="referral-credited-no">
                        <span className="inline-flex items-center gap-1 text-red-400">
                          <XCircle className="w-3 h-3" /> Blocked
                        </span>
                        {badgeForReason(r.reason)}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {total > perPage && (
          <div className="mt-4 flex items-center justify-between text-sm text-gray-400">
            <div>Page {page} of {totalPages}</div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1 || loading}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded flex items-center gap-1">
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <button type="button" onClick={() => setPage(p => p + 1)}
                disabled={page >= totalPages || loading}
                className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-40 rounded flex items-center gap-1">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReferralsPage;
